/**
 * Request shapes and result grouping for the global search (#2477). Pure, so
 * the wire contract is unit-testable without a component.
 *
 * Every request reuses an existing local-D1 list endpoint (no Shopify /
 * Onfleet call): the orders list `search`, and two exact-field filters on the
 * delivery-job list. The job list's free-text `search` is not used because it
 * also matches notes and line items, and could not tell the two groups apart.
 */
import type { FulfillmentJob } from '@/services';
import type { ListRequestParams } from '@/services/list-request';

export const MIN_SEARCH_CHARS = 2;
const ORDER_LIMIT = 5;
const JOB_LIMIT = 5;
/** Read a wider page than shown: N jobs fold into one driver task. */
const TASK_FETCH_LIMIT = 10;
const TASK_LIMIT = 5;
const JOB_SORT = 'shipping_date:desc';

export interface SearchRequests {
  orders: ListRequestParams;
  jobs: ListRequestParams;
  tasks: ListRequestParams;
}

export function buildSearchRequests(term: string): SearchRequests {
  // `name like` matches the order number only; a job name is `<order>-<MMDD>`.
  const jobTerm = term.replace(/-\d{4}$/, '');
  return {
    orders: { search: term, limit: ORDER_LIMIT },
    jobs: { limit: JOB_LIMIT, sort: JOB_SORT, extraParams: { name: `like:${jobTerm}` } },
    tasks: {
      limit: TASK_FETCH_LIMIT,
      sort: JOB_SORT,
      extraParams: { onfleet_task_short_id: `like:${term}` },
    },
  };
}

export interface TaskHit {
  taskId: string;
  shortId: string;
  driverName: string | null;
  jobCount: number;
}

/**
 * De-duplicate job rows by driver task (ADR-0026 D2), first-seen order. A row
 * without a short id is skipped: the item opens the job list searched by it.
 */
export function groupTasks(rows: readonly FulfillmentJob[]): TaskHit[] {
  const byTask = new Map<string, TaskHit>();
  for (const row of rows) {
    if (!row.onfleet_task_id || !row.onfleet_task_short_id) continue;
    const hit = byTask.get(row.onfleet_task_id);
    if (hit) {
      hit.jobCount += 1;
    } else {
      byTask.set(row.onfleet_task_id, {
        taskId: row.onfleet_task_id,
        shortId: row.onfleet_task_short_id,
        driverName: row.driver_name,
        jobCount: 1,
      });
    }
  }
  return [...byTask.values()].slice(0, TASK_LIMIT);
}
